import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSessionStorage } from 'usehooks-ts';

import { FactsTable } from './components/facts-table';
import { FactsTableHeader } from './components/facts-table-header';
import { FactsTableLoader } from './components/facts-table-loader';
import type { PageCursors } from '../../../../../../../../../backend/src/matter/subset/dto/response-get-subset.facts.dto';
import { RunType } from '../../../../../../../../../backend/src/run/run-type';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetCaseIssues } from '@/api/queries/useGetCaseThemes';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetPersonNames } from '@/api/queries/useGetPersonNames';
import { useGetSubset } from '@/api/queries/useGetSubset';
import useGetSubsetFactAllIds from '@/api/queries/useGetSubsetFactAllIds';
import useGetSubsetFacts from '@/api/queries/useGetSubsetFacts';
import { useGetSubsetOverview } from '@/api/queries/useGetSubsetOverview';
import { useIndexedDb } from '@/hooks/useIndexedDb';
import { useFactNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { CursorPagination } from '@/new/components/cursor-pagination';
import { FactDocViewer } from '@/new/components/doc-viewer/fact-doc-viewer';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { FactFilters, Relevance } from '@/types';
import { buildFactFilterPills } from '@/utils/buildFilterPills';
import { removeFilterValue } from '@/utils/filterUtils';

const SELECTION_CAP = 10_000;

export function Facts() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  const [docId, setDocId] = useState<string | null>(null);
  const [factId, setFactId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  // Map of known cursors for opensearch pagination.
  const [knownCursors, setKnownCursors] = useState<Map<number, PageCursors>>(new Map());

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialise filters with sessionStorage persistence
  const defaultFilters: FactFilters = {
    docIds: [],
    excludedDocIds: [],
    relevance: [],
    classification: [],
    keyFacts: false,
    disputedFacts: false,
    startDate: null,
    endDate: null,
    runIds: [],
    personIds: [],
    documentTypes: [],
    issueIds: [],
    searchQuery: '',
    factIds: [],
  };
  const [filters, setFilters] = useSessionStorage<FactFilters>(`case-${subsetId}-facts-table-filters`, defaultFilters);
  const [searchQueryInput, setSearchQueryInput] = useState(filters.searchQuery ?? '');
  const [selectedFactIds, setSelectedFactIds] = useState<Set<string>>(new Set());

  const { db, selectMultipleFacts, getAllSelectedFactIds, selectFact, deselectFact, deselectAllFacts } = useIndexedDb();

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setKnownCursors(new Map());
  }, []);

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useFactNaturalLanguageFilters({
    matterId,
    subsetId,
    onFiltersChange: (newFilters: FactFilters) => {
      setFilters(newFilters);
      resetPagination();
    },
    onSearchChange: (searchQuery: string) => {
      setFilters((prev) => ({ ...prev, searchQuery: searchQuery }));
    },
  });

  const { data: personNames } = useGetPersonNames(matterId, subsetId, filters.personIds ?? []);
  const personsMapRef = useRef<Map<string, string>>(new Map());
  const personsMap = useMemo(() => {
    if (personNames) {
      for (const { id, name } of personNames) {
        personsMapRef.current.set(id, name);
      }
    }
    return personsMapRef.current;
  }, [personNames]);
  const { data: issuesWithCounts = [] } = useGetCaseIssues({ matterId, subsetId });
  const issuesMap = useMemo(() => new Map(issuesWithCounts.map((t) => [t.id, t.name])), [issuesWithCounts]);
  const knownCursorsArray = useMemo(() => Array.from(knownCursors.values()), [knownCursors]);

  // Subset is "freshly created" if it was made in the last 5 minutes
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { data: subsetOverview } = useGetSubsetOverview(matterId, subsetId);
  const isFreshlyCreated = useMemo(() => {
    if (!subset?.createdDate) return false;
    return Date.now() - new Date(subset.createdDate).getTime() < 5 * 60 * 1000;
  }, [subset?.createdDate]);

  const {
    data: responseFacts,
    isLoading,
    refetch: refetchCaseFacts,
  } = useGetSubsetFacts({
    matterId,
    subsetId,
    searchQuery: filters.searchQuery ?? undefined,
    page: currentPage,
    knownCursors: knownCursorsArray,
    filters,
    // Poll while the subset is still indexing into OS
    refetchInterval: isFreshlyCreated ? 10_000 : false,
  });

  // Merge cursors returned by the backend into the FE map.
  useEffect(() => {
    if (!responseFacts || responseFacts.cursors.length === 0) return;
    setKnownCursors((prev) => {
      const next = new Map(prev);
      for (const entry of responseFacts.cursors) {
        next.set(entry.page, entry);
      }
      return next;
    });
  }, [responseFacts]);
  const { data: matter } = useGetMatterV2(matterId);
  const { data: runs } = useGetMatterRuns(matterId);

  // Latch: once facts have loaded, keep all-IDs query enabled permanently.
  // Without this, page changes cause responseFacts to briefly become undefined
  // (new query key = new cache entry), bouncing enabled false→true and refetching.
  const hasLoadedFacts = useRef(false);
  if (responseFacts) hasLoadedFacts.current = true;

  // Preserve pagination metadata across page changes so the pagination bar
  // and counts stay visible while the table shows skeletons.
  const paginationRef = useRef({ totalPages: 0, totalCount: 0, totalUnfilteredCount: 0 });
  if (responseFacts) {
    paginationRef.current = {
      totalPages: responseFacts.totalPages,
      totalCount: responseFacts.totalCount,
      totalUnfilteredCount: responseFacts.totalUnfilteredCount,
    };
  }
  const { totalPages, totalCount, totalUnfilteredCount } = paginationRef.current;

  // If a mutation shrinks the result set (e.g. archive, un-key under a key
  // filter), the user's currentPage may end up greater than totalPages and
  // they'd see an empty table. Snap them to a valid page:
  //   - totalPages === 0 (no results match): reset to page 1.
  //   - currentPage > totalPages (mid-table archive): clamp to last page.
  useEffect(() => {
    if (!responseFacts || isLoading) return;
    if (currentPage > Math.max(totalPages, 1)) {
      setCurrentPage(Math.max(totalPages, 1));
    }
  }, [responseFacts, isLoading, totalPages, currentPage]);

  // Show a syncing pill when the subset is freshly created and the PG-backed
  // total fact count (from the overview endpoint) hasn't yet matched the
  // OS-backed count.
  const isSyncing =
    isFreshlyCreated &&
    subsetOverview !== undefined &&
    responseFacts !== undefined &&
    subsetOverview.totalFacts !== totalUnfilteredCount;

  const { data: allIdsData, isLoading: isAllIdsLoading } = useGetSubsetFactAllIds({
    matterId,
    subsetId,
    searchQuery: filters.searchQuery ?? undefined,
    filters,
    enabled: hasLoadedFacts.current,
  });

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Facts', path: 'facts' },
  });

  // Load selected facts from IndexedDB
  useEffect(() => {
    async function loadSelectedFacts() {
      if (!db || !subsetId) return;
      const selectedIds = await getAllSelectedFactIds(subsetId);
      setSelectedFactIds(new Set(selectedIds));
    }
    loadSelectedFacts();
  }, [db, subsetId, getAllSelectedFactIds]);

  // Handlers
  const handleToggleAll = async ({ checked: toggleValue }: { checked: boolean }): Promise<void> => {
    if (!subsetId) return;

    trackCustomEvent(toggleValue ? TrackingEventsV2.FactToggleAllOn : TrackingEventsV2.FactToggleAllOff, {
      matterId,
      isAllDocuments: subset?.isAllDocuments,
    });

    if (toggleValue) {
      if (!allIdsData) return;

      await selectMultipleFacts(subsetId, allIdsData.factIds);
      const selectedIds = await getAllSelectedFactIds(subsetId);
      setSelectedFactIds(new Set(selectedIds));
    } else {
      await deselectAllFacts(subsetId);
      const selectedIds = await getAllSelectedFactIds(subsetId);
      setSelectedFactIds(new Set(selectedIds));
    }
  };

  const handleFactSelectionChange = useCallback(
    async (factId: string, selected: boolean) => {
      // Enforce selection cap
      if (selected && selectedFactIds.size >= SELECTION_CAP) {
        toast.warning('Maximum of 10,000 facts can be selected at once.');
        return;
      }

      // Optimistic update for responsive UI
      setSelectedFactIds((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(factId);
        } else {
          next.delete(factId);
        }
        return next;
      });

      // Persist to IndexedDB with rollback on error
      try {
        if (selected) {
          await selectFact(subsetId, factId);
        } else {
          await deselectFact(subsetId, factId);
        }
      } catch (error) {
        // Rollback on error
        setSelectedFactIds((prev) => {
          const next = new Set(prev);
          if (selected) {
            next.delete(factId);
          } else {
            next.add(factId);
          }
          return next;
        });
        console.error('Failed to update fact selection:', error);
      }
    },
    [subsetId, selectFact, deselectFact, selectedFactIds.size],
  );

  const toggleAllHandler = async () => {
    if (allRowsChecked) {
      await handleToggleAll({ checked: false });
    } else {
      await handleToggleAll({ checked: true });
    }
  };

  function deselectAllFactsFromDb() {
    if (!subsetId) return;
    deselectAllFacts(subsetId);
    setSelectedFactIds(new Set());
  }

  const onSearchCall = async (searchValue?: string, useNaturalLanguage?: boolean) => {
    trackCustomEvent(TrackingEventsV2.FactSearch, { matterId, isAllDocuments: subset?.isAllDocuments });

    const value = searchValue ?? searchQueryInput;

    if (useNaturalLanguage && value.trim()) {
      buildFiltersWithNL(value);
      return;
    }

    // Regular search — reset to page 1
    resetPagination();

    // Close the doc viewer if it is open
    setFilters((prev) => ({ ...prev, searchQuery: value }));
    setSearchQueryInput(value);
  };

  const clearSearch = async () => {
    setSearchQueryInput('');
    setFilters((prev) => ({ ...prev, searchQuery: '' }));
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  const handleRemoveFilter = (filterKey: string, value?: any) => {
    if (filterKey === 'searchQuery') {
      setSearchQueryInput('');
    }
    if (filterKey === 'personIds') {
      searchParams.delete('personId', value);
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'issueIds') {
      searchParams.delete('issueIds', value);
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'keyFacts') {
      searchParams.delete('viewKeyFacts');
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'relevance' && value === Relevance.Relevant) {
      searchParams.delete('viewRelevantFacts');
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'threadId') {
      searchParams.delete('threadId');
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'factIds') {
      searchParams.delete('factIds', value);
      setSearchParams(searchParams, { replace: true });
    }

    setFilters((prev) => removeFilterValue(prev, filterKey as keyof FactFilters, value));

    // Reset pagination when filters change
    resetPagination();
  };

  const openDocViewer = (docId: string, factId: string) => {
    setIsDocViewerOpen(true);
    setDocId(docId);
    setFactId(factId);
  };

  // Pagination
  const goNextPage = () => {
    if (currentPage < paginationRef.current.totalPages) setCurrentPage(currentPage + 1);
  };
  const goPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const goToStart = () => setCurrentPage(1);
  const goToEnd = () => {
    if (paginationRef.current.totalPages > 0) setCurrentPage(paginationRef.current.totalPages);
  };
  // FactsTableHeader resets pagination after destructive actions (e.g. cluster).
  const goToPage = (_val: number) => resetPagination();

  const handleClearAllFilters = () => {
    setFilters(defaultFilters);
    setSearchQueryInput('');

    // Clear URL params for key facts and disputed facts
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('viewKeyFacts');
    newParams.delete('viewDisputedFacts');
    newParams.delete('viewRelevantFacts');
    newParams.delete('issueIds');
    newParams.delete('threadId');
    // Remove all personId and factIds params
    const allKeys = Array.from(newParams.keys());
    allKeys.forEach((key) => {
      if (key === 'personId' || key === 'factIds') {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams, { replace: true });

    // Reset pagination to first page
    resetPagination();
  };

  // useEffects for query param filters
  useEffect(() => {
    const personIds = searchParams.getAll('personId');
    if (personIds.length === 0) {
      return;
    }
    setFilters((prevFilters) => ({ ...prevFilters, personIds }));
  }, [searchParams]);

  useEffect(() => {
    const factIds = searchParams.getAll('factIds');
    if (factIds.length === 0) {
      return;
    }
    setFilters((prevFilters) => ({ ...prevFilters, factIds }));
  }, [searchParams]);

  useEffect(() => {
    if (!searchParams.has('threadId')) {
      return;
    }
    const threadId = searchParams.get('threadId');
    setFilters((prevFilters) => {
      if (prevFilters.threadId === (threadId ?? undefined)) {
        return prevFilters;
      }
      return { ...prevFilters, threadId: threadId ?? undefined };
    });
  }, [searchParams]);

  useEffect(() => {
    const issueIds = searchParams.getAll('issueIds');
    if (issueIds.length === 0) {
      return;
    }
    setFilters((prevFilters) => ({ ...prevFilters, issueIds }));
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('viewKeyFacts') !== 'true') {
      return;
    }
    setFilters((prevFilters) => {
      if (prevFilters.keyFacts) {
        return prevFilters;
      }
      return { ...prevFilters, keyFacts: true };
    });
  }, [searchParams, setFilters]);

  useEffect(() => {
    if (searchParams.get('viewRelevantFacts') !== 'true') {
      return;
    }
    setFilters((prevFilters) => {
      const hasRelevantOnly =
        Array.isArray(prevFilters.relevance) &&
        prevFilters.relevance.length === 1 &&
        prevFilters.relevance[0] === Relevance.Relevant;
      if (hasRelevantOnly) {
        return prevFilters;
      }
      return { ...prevFilters, relevance: [Relevance.Relevant] };
    });
  }, [searchParams, setFilters]);

  // Check if all facts on current page are selected
  const allRowsChecked = useMemo(() => {
    if (!responseFacts?.facts || responseFacts.facts.length === 0) {
      return false;
    }
    return responseFacts.facts.every((fact) => selectedFactIds.has(fact.id));
  }, [responseFacts?.facts, selectedFactIds]);

  useEffect(() => {
    trackPageView(PageName.Facts);
  }, []);

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <FactsTableHeader
            // Search
            globalFilter={searchQueryInput}
            clearSearch={clearSearch}
            setGlobalFilter={setSearchQueryInput}
            onSearchCall={onSearchCall}
            refetchCaseFacts={refetchCaseFacts}
            goToPage={goToPage}
            activeFilters={filters}
            setFilters={setFilters}
            caseRuns={runs?.filter((run) => run.type === RunType.Regular || run.type === RunType.AddDocs) ?? []}
            onClusterComplete={() => {
              refetchCaseFacts();
            }}
            processingNL={isProcessingNLFiltersQuery}
            filterCount={
              Object.entries(filters).filter(
                ([key, value]) =>
                  // Exclude keyFacts and disputedFacts when false
                  !((key === 'keyFacts' || key === 'disputedFacts') && value === false) &&
                  value !== undefined &&
                  value !== null &&
                  value !== '' &&
                  !(Array.isArray(value) && value.length === 0),
              ).length
            }
            selectedFactIds={selectedFactIds}
            setSelectedFactIds={setSelectedFactIds}
            totalCount={totalCount}
            totalUnfilteredCount={totalUnfilteredCount}
          />

          <FilterPills
            pills={buildFactFilterPills(filters, handleRemoveFilter, personsMap, issuesMap)}
            onClearAll={handleClearAllFilters}
          />

          <div className="flex items-center justify-between pt-4 pb-2">
            <div className="flex items-center gap-3 pl-1 text-xs text-gray-500">
              {allIdsData?.capped ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Checkbox checked={false} disabled />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Selection is limited to 10,000 facts. Apply filters to narrow results.
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Checkbox
                  checked={allRowsChecked}
                  onCheckedChange={toggleAllHandler}
                  disabled={isLoading || isAllIdsLoading}
                />
              )}
              <p>
                {allIdsData?.capped && selectedFactIds.size === 0 ? (
                  <span>10,000+ facts — apply filters to select all</span>
                ) : (
                  <>
                    <strong>{selectedFactIds.size.toLocaleString()}</strong> Facts selected
                  </>
                )}
              </p>

              <Button size="sm" disabled={selectedFactIds.size === 0} onClick={deselectAllFactsFromDb}>
                Clear All ({selectedFactIds.size}) Selected Facts
              </Button>

              {isSyncing && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-xs text-sky-600">
                      <Icon className="lucide--loader-2 h-3 w-3 animate-spin duration-1500" />
                      Syncing facts
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>This subset was just created, facts are still being processed.</TooltipContent>
                </Tooltip>
              )}
            </div>

            <CursorPagination
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading}
              onPrev={goPrevPage}
              onNext={goNextPage}
              onSkipToStart={goToStart}
              onSkipToEnd={goToEnd}
              totalCount={totalUnfilteredCount}
              filteredCount={totalCount}
              entityName="facts"
            />
          </div>
        </PageContainer>

        <div className="flex w-full flex-1 gap-1 overflow-hidden">
          {isLoading ? (
            <FactsTableLoader />
          ) : (
            <div
              className={`h-full overflow-y-scroll transition-all duration-300 ${isDocViewerOpen ? 'w-7/12' : 'w-full'}`}
            >
              <FactsTable
                selectedFactId={factId ?? ''}
                facts={responseFacts?.facts || []}
                openDocViewer={openDocViewer}
                selectedFactIds={selectedFactIds}
                onFactSelectionChange={handleFactSelectionChange}
              />
            </div>
          )}
          <div
            className={`transition-all duration-300 ease-in-out ${
              isDocViewerOpen ? 'mr-2 mb-2 w-5/12 opacity-100' : 'w-0 opacity-0'
            } overflow-hidden rounded-md border border-gray-300 bg-white`}
          >
            <div
              className={`h-full w-full transition-transform duration-300 ease-in-out ${
                isDocViewerOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              {isDocViewerOpen && docId && matterId && factId && (
                <FactDocViewer
                  docId={docId}
                  factId={factId}
                  onClose={() => {
                    setDocId(null);
                    setFactId(null);
                    setIsDocViewerOpen(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
